import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Spin, ConfigProvider } from 'antd';
import MainLayout from './components/Layout/MainLayout';
import LoginPage from './pages/Login';
import LoginMinimalPage from './pages/LoginMinimal';
import LoginTechPage from './pages/LoginTech';
import LoginGlassPage from './pages/LoginGlass';
import { useAuthStore } from './store/authStore';
import { themeConfig } from './theme/themeConfig';
import { ThemeAdaptorProvider } from './providers/ThemeAdaptorProvider';
import { checkRoutePermission, findRouteConfig } from './config/routeConfig';

/**
 * 验证JWT token是否过期
 * Validate if JWT token is expired
 */
const isTokenExpired = (token: string): boolean => {
  try {
    // JWT格式: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) return true;
    
    // 解码payload (base64)
    const payload = JSON.parse(atob(parts[1]));
    
    // 检查exp字段（过期时间，Unix时间戳，单位秒）
    if (!payload.exp) return false; // 如果没有exp字段，假设不过期
    
    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp < currentTime;
  } catch (error) {
    console.error('[Auth] Token解析失败:', error);
    return true; // 解析失败视为过期
  }
};

// 受保护的路由组件 / Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, token, user, logout } = useAuthStore();
  const location = useLocation();
  const [isHydrated, setIsHydrated] = useState(false);
  const [isValidating, setIsValidating] = useState(true);

  // 等待 Zustand persist 从 localStorage 恢复状态
  // Wait for Zustand persist to hydrate state from localStorage
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsHydrated(true);
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  // 验证认证状态
  // Validate authentication state
  useEffect(() => {
    if (!isHydrated) return;

    console.log('[Auth Debug] 开始验证认证状态...');
    console.log('[Auth Debug] isAuthenticated:', isAuthenticated);
    console.log('[Auth Debug] token:', token ? `${token.substring(0, 20)}...` : 'null');
    console.log('[Auth Debug] user:', user ? user.username : 'null');

    // 如果有token，检查是否过期
    if (token && isTokenExpired(token)) {
      console.log('[Auth Debug] Token已过期，清除认证状态');
      logout();
      setIsValidating(false);
      return;
    }

    // 如果声称已认证但缺少必要信息，清除状态
    if (isAuthenticated && (!token || !user)) {
      console.log('[Auth Debug] 认证状态不完整，清除认证状态');
      logout();
      setIsValidating(false);
      return;
    }

    setIsValidating(false);
  }, [isHydrated, isAuthenticated, token, user, logout]);

  // 在 hydration 和验证完成之前显示加载状态
  if (!isHydrated || isValidating) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: '#f0f2f5'
      }}>
        <Spin size="large" tip="加载中..." />
      </div>
    );
  }

  // 验证认证状态的完整性 - 必须同时有 token 和 user
  const isValidAuth = isAuthenticated && token && user && !isTokenExpired(token);
  
  if (!isValidAuth) {
    console.log('[Auth Debug] 认证无效，重定向到登录页');
    return <Navigate to="/login" replace />;
  }
  
  // ✅ 新增：检查路由权限
  const currentPath = location.pathname;
  const userPermissions = user?.permissions || [];
  
  // 跳过权限检查的路径
  const skipPermissionCheck = [
    '/dashboard',
    '/permission-denied',
    '/responsive-test',
    '/sync-demo'
  ];
  
    if (!skipPermissionCheck.includes(currentPath) && currentPath !== '/') {
    const hasPermission = checkRoutePermission(currentPath, userPermissions);
    
    if (!hasPermission) {
      console.log('[Auth Debug] 无权限访问:', currentPath);
      console.log('[Auth Debug] 用户权限:', userPermissions);
      const routeConfig = findRouteConfig(currentPath);
      return (
        <Navigate
          to="/permission-denied"
          replace
          state={{
            module: currentPath.split('/')[1] || 'unknown',
            operation: 'view',
            requiredPermission: routeConfig?.permission ?? null,
          }}
        />
      );
    }
  }
  
  return <>{children}</>;
};

function App() {
  return (
    <ThemeAdaptorProvider>
      <ConfigProvider theme={themeConfig}>
        <div className="App">
          <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/login-minimal" element={<LoginMinimalPage />} />
          <Route path="/login-tech" element={<LoginTechPage />} />
          <Route path="/login-glass" element={<LoginGlassPage />} />
          <Route 
            path="/*" 
            element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            } 
          />
        </Routes>
        </div>
      </ConfigProvider>
    </ThemeAdaptorProvider>
  );
}

export default App;
